import type { AutocompleteDeleteEvent } from '@ingenico/react-ui/form-section';
import { AutocompleteControl } from '@ingenico/react-ui/form-section';
import { Checkbox } from '@mui/material';
import { isString, toString } from 'lodash-es';
import type { HTMLAttributes, ReactNode, SyntheticEvent } from 'react';
import { useCallback } from 'react';
import type { SmartFieldCategoryOption, SmartFieldsResource } from '../../../domain/models/smartFields.model';
import { checkIsOptionEqualToValue } from '../parameter-module-map-smart-fields-modal/ParameterModuleMapSmartFieldsModal.util';
import styles from './ParameterModuleSmartFieldContent.module.scss';
import { useParameterModuleSmartFieldSearchContent } from './ParameterModuleSmartFieldSearchContent.vm';

export function ParameterModuleSmartFieldSearchContent({
  hasError,
  isLoading,
  componentLabel,
  placeholder,
  handleChange,
  options,
  selected,
  isCreation,
  isCategoryFeatureEnabled,
  categories: {
    categoriesLabel,
    categoriesNoText,
    categoriesPlaceholder,
    categoryOptions,
    handleChangeCategories,
    selectedCategories,
    isLoadingCategories,
    handleCategorySearchChange,
  },
  handleSmartfieldSearchChange,
}: {
  readonly hasError: boolean | undefined;
  readonly isLoading: boolean | undefined;
  readonly componentLabel: string;
  readonly placeholder: string;
  readonly isCreation: boolean;
  readonly handleChange: (newSmartField?: SmartFieldsResource) => void;
  readonly options: SmartFieldsResource[];
  readonly selected: SmartFieldsResource | undefined;
  readonly isCategoryFeatureEnabled: boolean;
  readonly categories: {
    readonly categoriesLabel: string;
    readonly categoriesPlaceholder: string;
    readonly categoriesNoText: string;
    readonly categoryOptions: SmartFieldCategoryOption[];
    readonly handleChangeCategories: (newCategories: SmartFieldCategoryOption[]) => void;
    readonly selectedCategories: SmartFieldCategoryOption[];
    readonly isLoadingCategories: boolean;
    readonly handleCategorySearchChange: (search: string) => void;
  };
  readonly handleSmartfieldSearchChange: (search: string) => void;
}): ReactNode {
  const {
    handleCategoryInputChange,
    handleSmartfieldInputChange,
    handleSmartfieldBlur,
    getOptionLabel,
    handleCategoryDelete,
  } = useParameterModuleSmartFieldSearchContent(handleCategorySearchChange, handleSmartfieldSearchChange, selected);

  const renderOption = useCallback(
    (props: HTMLAttributes<HTMLLIElement>, option: SmartFieldsResource) => (
      <li {...props} key={`${option.name}-${option.id}`.toLocaleLowerCase()}>
        {option.name}
      </li>
    ),
    [],
  );

  const handleChangeField = useCallback(
    (_: SyntheticEvent, data: SmartFieldsResource | null) => {
      handleChange(data ?? undefined);
    },
    [handleChange],
  );

  const checkIsCategoryFieldOptionEqualToValue = useCallback(
    (option: string | SmartFieldCategoryOption, value: string | SmartFieldCategoryOption) => {
      const optionId = isString(option) ? option : option.id;
      const valueId = isString(value) ? value : value.id;
      return optionId === valueId;
    },
    [],
  );

  const handleChangeCategoryField = useCallback(
    (_: SyntheticEvent, data: SmartFieldCategoryOption[]) => {
      handleChangeCategories(data);
    },
    [handleChangeCategories],
  );

  return (
    <div className={styles.fieldWrapper}>
      {isCategoryFeatureEnabled && (
        <AutocompleteControl
          multiple
          disableCloseOnSelect
          error={hasError}
          clearOnBlur={false}
          filterSelectedOptions={false}
          fullWidth
          getOptionLabel={getOptionLabel}
          input={{
            label: categoriesLabel,
            name: '',
            component: 'Autocomplete',
          }}
          inputProps={{
            'data-testid': `field-smart-field-modal`,
            placeholder: categoriesPlaceholder,
            className: styles.categories,
          }}
          isOptionEqualToValue={checkIsCategoryFieldOptionEqualToValue}
          renderTagsTranslateLabel={getOptionLabel}
          loading={isLoadingCategories}
          onChange={handleChangeCategoryField}
          onInputChange={(event, value, reason) => {
            if (reason === 'input' || reason === 'clear' || reason === 'reset') {
              handleCategoryInputChange(event, value, reason);
            }
          }}
          noOptionsText={categoriesNoText}
          onDelete={(event: AutocompleteDeleteEvent) =>
            handleCategoryDelete(event, selectedCategories, handleChangeCategories)
          }
          options={categoryOptions}
          renderOption={(props, option: string | SmartFieldCategoryOption, { selected: selectedCat }) => {
            const optionLabel = isString(option) ? option : option.label;
            return (
              <li {...props} key={optionLabel}>
                <Checkbox key={optionLabel} checked={selectedCat} style={{ marginRight: 8 }} />
                {optionLabel}
              </li>
            );
          }}
          value={selectedCategories}
          disabled={isCreation}
          chipVariant="filled"
        />
      )}
      <AutocompleteControl<SmartFieldsResource, false>
        error={hasError}
        fullWidth
        getOptionLabel={(option: SmartFieldsResource | string) => {
          if (isString(option)) {
            return option;
          }
          return toString(option?.name);
        }}
        input={{
          label: componentLabel,
          name: '',
          component: 'Autocomplete',
        }}
        inputProps={{
          'data-testid': `field-smart-field-modal`,
          className: styles.smartField,
          placeholder,
        }}
        isOptionEqualToValue={checkIsOptionEqualToValue}
        loading={isLoading}
        onChange={handleChangeField}
        onInputChange={(event, value, reason) => {
          if (reason === 'input' || reason === 'clear' || reason === 'reset') {
            handleSmartfieldInputChange(event, value, reason);
          }
        }}
        onBlur={handleSmartfieldBlur}
        options={options}
        renderOption={renderOption}
        value={options.find((option) => option.id === selected?.id) ?? null}
        disabled={isCreation}
        labelClassName={styles.smartFieldLabel}
      />
    </div>
  );
}
